import {
	AccountDetailFields,
	ValidateFieldOptions,
	ValidationResult,
} from '@shared/types/enach.types';
import {
	AccountFormFieldValidations,
	ACCOUNT_NUMBER_MAX_LENGTH,
	IFSC_MAX_LENGTH,
	ACCOUNT_HOLDER_NAME_MAX_LENGTH,
} from '@shared/constants';
import {
	numericOnly,
	alphanumericOnly,
	alphanumericWithSpace,
	limit,
} from '@shared/utils/validation.utils';

export const ENACH_FIELD_SANITIZERS: Record<
	AccountDetailFields,
	(value: string) => string
> = {
	[AccountDetailFields.ACCOUNT_NUMBER]: (value) =>
		limit(numericOnly(value), ACCOUNT_NUMBER_MAX_LENGTH),
	[AccountDetailFields.IFSC]: (value) =>
		limit(alphanumericOnly(value).toUpperCase(), IFSC_MAX_LENGTH),
	[AccountDetailFields.ACCOUNT_HOLDER_NAME]: (value) =>
		limit(alphanumericWithSpace(value), ACCOUNT_HOLDER_NAME_MAX_LENGTH),
	[AccountDetailFields.ACCOUNT_TYPE]: (value) => value,
};

export const validateEnachField = (
	fieldName: AccountDetailFields,
	value: string,
	options: ValidateFieldOptions = {},
): ValidationResult => {
	const { forceValidation = false } = options;
	const rules = AccountFormFieldValidations[fieldName];

	if (!rules) {
		return { isValid: true, errorMessage: '' };
	}

	for (const rule of rules) {
		if (!rule.active) {
			continue;
		}

		let isRuleValid = true;

		const showErrorThreshold = rule.showErrorAfterLength ?? 0;
		const shouldShowError =
			forceValidation || value.length >= showErrorThreshold;

		switch (rule.type) {
			case 'LENGTH':
				isRuleValid =
					value.length >= rule.minLength && value.length <= rule.maxLength;
				break;

			case 'REGEX':
				isRuleValid = rule.regex.test(value);
				break;

			default:
				break;
		}

		if (!isRuleValid && shouldShowError) {
			return { isValid: false, errorMessage: rule.message };
		}
	}

	return { isValid: true, errorMessage: '' };
};

export const validateAllEnachFields = (
	formData: Record<AccountDetailFields, string>,
): Record<string, string> => {
	const errors: Record<string, string> = {};

	for (const field of Object.values(AccountDetailFields)) {
		const value = formData[field] ?? '';
		const result = validateEnachField(field, value, {
			forceValidation: true,
		});
		if (!result.isValid) {
			errors[field] = result.errorMessage;
		}
	}

	return errors;
};
